/**
 * Serviço de geocodificação híbrida
 * Combina OpenCage + ViaCEP para máxima eficiência e economia
 */

import type {
  GeocodingResult,
  GeocodingOptions,
  ConsistencyCheck
} from '../types/geocoding';
import type {
  Location,
  Coordinates,
  GeographicValidationResult
} from '../types/base';
import { GeographicValidator } from '../types/utils';
import {
  DEFAULT_GEOCODING_CONFIG
} from '../types/config';
import { LocationUtils } from '../types/utils';
import { viaCEPService } from './viacep';
import { geocodeAddress as geocodeWithOpenCage } from './geocoding';
import { getDistance } from 'geolib';
import { logger } from '../utils/logger';

export class HybridGeocodingService {
  private readonly config = DEFAULT_GEOCODING_CONFIG;

  /**
   * Geocodifica automaticamente escolhendo a melhor estratégia
   */
  async geocode(
    input: string | Location,
    options: GeocodingOptions = {}
  ): Promise<GeocodingResult> {
    const query = typeof input === 'string' ? input : input.endereco;
    const cep = typeof input === 'string' ? LocationUtils.extractCEP(input) : input.cep;

    logger.log(`🧠 Hybrid: Iniciando geocodificação inteligente`, {
      query: query.substring(0, 50) + (query.length > 50 ? '...' : ''),
      hasCEP: !!cep,
      strategy: this.detectStrategy(query, cep)
    });

    // Detecta automaticamente a melhor estratégia
    const strategy = this.detectStrategy(query, cep);

    switch (strategy) {
      case 'cep_only':
        return await this.geocodeByCEP(cep!, options);

      case 'hybrid_validation':
        return await this.geocodeWithValidation(query, cep!, options);

      case 'address_fallback':
        return await this.geocodeWithFallback(query, cep || null, options);

      default:
        try {
          return await this.geocodeByAddress(query, options);
        } catch (error) {
          // Se geocodeByAddress falhou (provavelmente coordenadas fora do Brasil)
          // Tenta usar CEP se disponível
          if (cep) {
            logger.warn('🔄 Endereço resultou em coordenadas fora do Brasil - tentando CEP', {
              address: query,
              cep,
              error: error instanceof Error ? error.message : error
            });

            try {
              return await this.geocodeByCEP(cep, options);
            } catch (cepError) {
              logger.error('❌ Falha tanto no endereço quanto no CEP', { error, cepError });
              throw error; // Relança o erro original do endereço
            }
          } else {
            logger.error('❌ Endereço fora do Brasil e sem CEP para fallback', { error });
            throw error;
          }
        }
    }
  }

  /**
   * Geocodifica usando apenas CEP (ViaCEP)
   */
  async geocodeByCEP(cep: string, options: GeocodingOptions = {}): Promise<GeocodingResult> {
    logger.log(`🇧🇷 Hybrid: Geocodificação por CEP`);

    try {
      const result = await viaCEPService.geocodeCEP(cep);
      return {
        ...result,
        source: 'viacep',
        method: 'cep'
      };
    } catch (error) {
      if (options.fallbackToOpenCage !== false) {
        logger.warn(`ViaCEP falhou, tentando OpenCage como fallback`);
        return await this.geocodeByAddress(cep, options);
      }
      throw error;
    }
  }

  /**
   * Geocodifica endereço com validação por CEP
   */
  async geocodeWithValidation(
    address: string,
    cep: string,
    options: GeocodingOptions = {}
  ): Promise<GeocodingResult> {
    logger.log(`✅ Hybrid: Geocodificação com validação cruzada`);

    try {
      // Geocodifica endereço e CEP em paralelo
      const [addressResult, cepResult] = await Promise.all([
        this.geocodeByAddress(address, options),
        this.geocodeByCEP(cep, options)
      ]);

      // Verifica consistência
      const consistency = this.checkConsistency(addressResult, cepResult);

      if (consistency.isConsistent) {
        logger.log(`✅ Dados consistentes, usando endereço (precisão alta)`);
        return {
          ...addressResult,
          source: 'hybrid',
          method: 'address',
          confidence: 'high'
        };
      } else {
        logger.warn(`⚠️ Inconsistência detectada (${consistency.distance}m), usando ${consistency.recommendation}`);

        const selectedResult = consistency.recommendation === 'use_address'
          ? addressResult
          : cepResult;

        return {
          ...selectedResult,
          source: 'hybrid',
          method: consistency.recommendation === 'use_address' ? 'address' : 'cep',
          confidence: 'medium'
        };
      }

    } catch (error) {
      logger.error(`❌ Hybrid: Erro na validação cruzada`, error);

      // Fallback: tenta endereço apenas
      if (options.fallbackToOpenCage !== false) {
        return await this.geocodeByAddress(address, options);
      }
      throw error;
    }
  }

  /**
   * Geocodifica endereço com fallback para CEP
   */
  async geocodeWithFallback(
    address: string,
    cep: string | null,
    options: GeocodingOptions = {}
  ): Promise<GeocodingResult> {
    logger.log(`🔄 Hybrid: Geocodificação com fallback`);

    try {
      // Tenta endereço primeiro
      const result = await this.geocodeByAddress(address, options);
      return {
        ...result,
        source: 'opencage',
        method: 'address'
      };

    } catch (error) {
      if (cep && options.fallbackToOpenCage !== false) {
        logger.warn(`OpenCage falhou, tentando ViaCEP como fallback`);
        const result = await this.geocodeByCEP(cep, options);
        return {
          ...result,
          method: 'fallback'
        };
      }
      throw error;
    }
  }

  /**
   * Geocodifica usando apenas endereço (OpenCage)
   */
  async geocodeByAddress(address: string, _options: GeocodingOptions = {}): Promise<GeocodingResult> {
    logger.log(`🌍 Hybrid: Geocodificação por endereço com validação brasileira`);

    const coords = await geocodeWithOpenCage(address);

    // VALIDAÇÃO CRÍTICA: Verifica se coordenadas estão no Brasil
    const validation = GeographicValidator.validateBrazilianCoordinates(coords);

    if (!validation.isWithinBrazil) {
      logger.error('❌ ALERTA: geocodeByAddress retornou coordenadas FORA DO BRASIL!', {
        address,
        coords,
        validation,
        country: 'Provavelmente não é Brasil'
      });

      // REJEITA coordenadas estrangeiras
      throw new Error(`Endereço "${address}" resulta em coordenadas fora do Brasil: ${coords.latitude}, ${coords.longitude}`);
    }

    logger.info('✅ Endereço geocodificado dentro do território brasileiro', {
      address,
      coords,
      validation: validation.warningLevel
    });

    return {
      latitude: coords.latitude,
      longitude: coords.longitude,
      formatted: address,
      source: 'opencage',
      confidence: 'high',
      method: 'address'
    };
  }

  /**
   * Verifica consistência entre resultado de endereço e CEP
   */
  checkConsistency(
    addressResult: GeocodingResult,
    cepResult: GeocodingResult
  ): ConsistencyCheck {
    const distance = getDistance(
      { lat: addressResult.latitude, lng: addressResult.longitude },
      { lat: cepResult.latitude, lng: cepResult.longitude }
    );

    const tolerance = this.config.addressCepToleranceMeters;
    const isConsistent = distance <= tolerance;

    let recommendation: ConsistencyCheck['recommendation'];
    if (isConsistent) {
      recommendation = 'use_address'; // Endereço é mais preciso
    } else if (distance <= tolerance * 2) {
      recommendation = 'use_address'; // Ainda dentro de tolerância ampliada
    } else if (distance <= tolerance * 5) {
      recommendation = 'use_cep'; // CEP pode ser mais confiável
    } else {
      recommendation = 'manual_review'; // Discrepância muito grande
    }

    return {
      isConsistent,
      distance,
      tolerance,
      addressResult,
      cepResult,
      recommendation
    };
  }

  /**
   * Detecta a melhor estratégia baseada nos dados disponíveis
   */
  private detectStrategy(
    address: string,
    cep: string | null | undefined
  ): 'cep_only' | 'hybrid_validation' | 'address_fallback' | 'address_only' {
    const analysis = LocationUtils.analyzeAddress(address);

    // Se é apenas CEP
    if (analysis.type === 'cep_only') {
      return 'cep_only';
    }

    // Se tem endereço + CEP e ambos parecem válidos
    if (analysis.type === 'address_with_cep' || (cep && analysis.confidence === 'high')) {
      return 'hybrid_validation';
    }

    // Se tem endereço sem CEP mas há CEP extraível ou fornecido
    if (cep && analysis.type === 'address_only') {
      return 'address_fallback';
    }

    // Apenas endereço
    return 'address_only';
  }

  /**
   * Geocodifica com validação geográfica brasileira
   */
  async geocodeWithBrazilValidation(
    input: string | Location,
    options?: GeocodingOptions
  ): Promise<GeocodingResult & { brazilValidation?: GeographicValidationResult }> {
    logger.info('🌎 Iniciando geocodificação com validação brasileira', { input, options });

    const result = await this.geocode(input, options);

    if (result && result.latitude && result.longitude) {
      const coordinates: Coordinates = {
        latitude: result.latitude,
        longitude: result.longitude
      };

      const validation = GeographicValidator.validateBrazilianCoordinates(coordinates);

      logger.info('🔍 Validação geográfica concluída', {
        coordinates,
        isWithinBrazil: validation.isWithinBrazil,
        warningLevel: validation.warningLevel,
        message: validation.message
      });

      // Log de aviso se coordenadas estão fora do Brasil
      if (!validation.isWithinBrazil) {
        logger.warn('⚠️ Coordenadas fora do território brasileiro', {
          coordinates,
          distanceFromCenter: validation.distanceFromCenter,
          nearestBorderDistance: validation.nearestBorderDistance,
          suggestion: GeographicValidator.suggestCorrection(coordinates).suggestion
        });
      }

      return {
        ...result,
        brazilValidation: validation
      };
    }

    return {
      ...result,
      brazilValidation: {
        isWithinBrazil: false,
        distanceFromCenter: 0,
        warningLevel: 'outside_brazil',
        message: 'Geocodificação falhou - não foi possível validar coordenadas',
        coordinates: { latitude: 0, longitude: 0 }
      }
    };
  }

  /**
   * Geocodifica seguindo lógica brasileira específica:
   * 1. Busca por endereço
   * 2. Se coordenadas estão no Brasil → verifica consistência com CEP
   * 3. Se coordenadas NÃO estão no Brasil → usa CEP brasileiro obrigatoriamente
   */
  async geocodeWithBrazilFallback(
    input: string | Location,
    options?: GeocodingOptions
  ): Promise<GeocodingResult & {
    brazilValidation?: GeographicValidationResult;
    usedFallback?: boolean;
    fallbackReason?: string;
    increasedTolerance?: boolean;
  }> {
    logger.info('🇧🇷 Iniciando geocodificação com lógica brasileira', { input, options });

    // Extrai CEP antes de qualquer coisa
    const locationObj = typeof input === 'string' ? { endereco: input } : input;
    const cep = ('cep' in locationObj ? locationObj.cep : null) || this.extractCEPFromAddress(locationObj.endereco);

    // ETAPA 1: Geocodificação inicial do endereço
    logger.info('🔍 ETAPA 1: Geocodificando endereço inicial', { input });
    const initialResult = await this.geocode(input, options);

    if (!initialResult || !initialResult.latitude || !initialResult.longitude) {
      // Se não conseguiu geocodificar, tenta CEP diretamente
      logger.warn('❌ Falha na geocodificação inicial - tentando CEP', { initialResult, cep });
      return await this.useCEPAsOnlyOption(cep, 'Falha na geocodificação do endereço');
    }

    const initialCoords: Coordinates = {
      latitude: initialResult.latitude,
      longitude: initialResult.longitude
    };

    logger.info('📍 RESULTADO INICIAL:', {
      coords: initialCoords,
      formatted: initialResult.formatted,
      source: initialResult.source
    });

    const validation = GeographicValidator.validateBrazilianCoordinates(initialCoords);

    logger.info('🔍 ETAPA 2: Validação territorial', {
      coords: initialCoords,
      isWithinBrazil: validation.isWithinBrazil,
      warningLevel: validation.warningLevel,
      message: validation.message
    });

    // ETAPA 2: Se coordenadas estão NO BRASIL
    if (validation.isWithinBrazil) {
      logger.info('✅ Coordenadas estão no Brasil - verificando consistência com CEP', {
        coords: initialCoords,
        cep
      });

      if (cep) {
        // Verifica consistência entre endereço e CEP
        const isConsistent = await this.checkAddressCEPConsistency(initialCoords, cep);

        if (isConsistent) {
          logger.info('✅ Endereço consistente com CEP - usando endereço');
          return {
            ...initialResult,
            brazilValidation: validation,
            usedFallback: false
          };
        } else {
          logger.warn('⚠️ Endereço inconsistente com CEP - usando CEP');
          return await this.useCEPAsCorrection(cep, 'Endereço inconsistente com CEP brasileiro');
        }
      } else {
        logger.info('✅ Coordenadas no Brasil e sem CEP para verificar - usando endereço');
        return {
          ...initialResult,
          brazilValidation: validation,
          usedFallback: false
        };
      }
    }

    // ETAPA 3: Se coordenadas estão FORA DO BRASIL
    logger.error('❌ CRÍTICO: Coordenadas fora do Brasil - usando CEP obrigatoriamente', {
      originalCoords: initialCoords,
      validation,
      endereco: locationObj.endereco,
      cep,
      source: initialResult.source,
      formatted: initialResult.formatted
    });

    return await this.useCEPAsOnlyOption(cep, 'Endereço resulta em coordenadas fora do Brasil', true);
  }

  /**
   * Usa CEP como única opção (quando endereço falha ou está fora do Brasil)
   */
  private async useCEPAsOnlyOption(
    cep: string | null,
    reason: string,
    increaseTolerance: boolean = false
  ): Promise<GeocodingResult & {
    brazilValidation?: GeographicValidationResult;
    usedFallback?: boolean;
    fallbackReason?: string;
    increasedTolerance?: boolean;
  }> {
    if (!cep) {
      logger.error('🚫 CEP não disponível para fallback', { reason, cep });
      throw new Error(`${reason} e CEP não disponível para fallback`);
    }

    logger.info('🔄 ETAPA 3: Usando CEP como única opção', {
      cep,
      reason,
      increaseTolerance
    });

    try {
      const cepResult = await this.geocodeBrazilianCEP(cep);

      logger.info('✅ CEP geocodificado com sucesso', {
        cep,
        result: {
          coords: { lat: cepResult.latitude, lng: cepResult.longitude },
          formatted: cepResult.formatted,
          confidence: cepResult.confidence
        }
      });

      const cepCoordinates: Coordinates = {
        latitude: cepResult.latitude,
        longitude: cepResult.longitude
      };

      const cepValidation = GeographicValidator.validateBrazilianCoordinates(cepCoordinates);

      if (!cepValidation.isWithinBrazil) {
        throw new Error(`CEP ${cep} também resulta em coordenadas fora do Brasil`);
      }

      logger.info('✅ Usando CEP brasileiro com sucesso', {
        cep,
        coords: cepCoordinates,
        confidence: cepResult.confidence,
        increaseTolerance
      });

      return {
        latitude: cepResult.latitude,
        longitude: cepResult.longitude,
        formatted: cepResult.formatted,
        source: 'viacep',
        confidence: cepResult.confidence,
        method: 'fallback',
        brazilValidation: cepValidation,
        usedFallback: true,
        fallbackReason: reason,
        increasedTolerance: increaseTolerance
      };
    } catch (error) {
      logger.error('❌ Erro ao usar CEP', { cep, error });
      throw error;
    }
  }

  /**
   * Usa CEP como correção (quando endereço está no Brasil mas inconsistente)
   */
  private async useCEPAsCorrection(
    cep: string,
    reason: string
  ): Promise<GeocodingResult & {
    brazilValidation?: GeographicValidationResult;
    usedFallback?: boolean;
    fallbackReason?: string;
  }> {
    try {
      const cepResult = await this.geocodeBrazilianCEP(cep);

      const cepCoordinates: Coordinates = {
        latitude: cepResult.latitude,
        longitude: cepResult.longitude
      };

      const cepValidation = GeographicValidator.validateBrazilianCoordinates(cepCoordinates);

      logger.info('✅ Usando CEP como correção', {
        cep,
        coords: cepCoordinates,
        confidence: cepResult.confidence
      });

      return {
        latitude: cepResult.latitude,
        longitude: cepResult.longitude,
        formatted: cepResult.formatted,
        source: 'viacep',
        confidence: cepResult.confidence,
        method: 'fallback',
        brazilValidation: cepValidation,
        usedFallback: true,
        fallbackReason: reason
      };
    } catch (error) {
      logger.error('❌ Erro ao usar CEP como correção', { cep, error });
      throw error;
    }
  }

  /**
   * Verifica se coordenadas do endereço são consistentes com a região do CEP
   */
  private async checkAddressCEPConsistency(
    addressCoords: Coordinates,
    cep: string
  ): Promise<boolean> {
    try {
      const cepResult = await this.geocodeBrazilianCEP(cep);

      const cepCoords: Coordinates = {
        latitude: cepResult.latitude,
        longitude: cepResult.longitude
      };

      // Calcula distância entre endereço e CEP
      const distance = getDistance(addressCoords, cepCoords);

      // Considera consistente se estão a menos de 10km (ajustável)
      const maxDistance = 10000; // 10km
      const isConsistent = distance <= maxDistance;

      logger.info('🔍 Verificação de consistência endereço-CEP', {
        addressCoords,
        cepCoords,
        distance,
        maxDistance,
        isConsistent
      });

      return isConsistent;
    } catch (error) {
      logger.warn('⚠️ Erro na verificação de consistência - assumindo inconsistente', { error });
      return false;
    }
  }  /**
   * Extrai CEP de uma string de endereço
   */
  private extractCEPFromAddress(endereco: string): string | null {
    return LocationUtils.extractCEP(endereco);
  }

  /**
   * Geocodifica usando dados do CEP brasileiro de forma inteligente
   */
  private async geocodeBrazilianCEP(cep: string): Promise<{
    latitude: number;
    longitude: number;
    formatted: string;
    confidence: 'high' | 'medium' | 'low';
  }> {
    logger.info('🇧🇷 Geocodificando CEP brasileiro', { cep });

    // 1. Busca dados do CEP no ViaCEP
    const cepData = await viaCEPService.getCEPData(cep);

    // 2. Estratégia em cascata para máxima precisão
    const strategies = [
      // Estratégia 1: Endereço completo (se logradouro disponível)
      cepData.logradouro ? `${cepData.logradouro}, ${cepData.bairro}, ${cepData.localidade}, ${cepData.uf}, Brasil` : null,

      // Estratégia 2: Bairro + Cidade (intermediária)
      cepData.bairro ? `${cepData.bairro}, ${cepData.localidade}, ${cepData.uf}, Brasil` : null,

      // Estratégia 3: Apenas cidade + estado (mais confiável)
      `${cepData.localidade}, ${cepData.uf}, Brasil`
    ].filter(Boolean);

    const { geocodeAddress } = await import('./geocoding');

    for (const [index, address] of strategies.entries()) {
      if (!address) continue;

      try {
        logger.info(`🎯 Tentativa ${index + 1}: ${address}`);

        const coords = await geocodeAddress(address);

        if (coords && coords.latitude && coords.longitude) {
          const validation = GeographicValidator.validateBrazilianCoordinates(coords);

          if (validation.isWithinBrazil) {
            const confidence = index === 0 ? 'high' : index === 1 ? 'medium' : 'low';

            logger.info(`✅ Sucesso na estratégia ${index + 1}`, {
              address,
              coords,
              confidence,
              validation: validation.warningLevel
            });

            return {
              latitude: coords.latitude,
              longitude: coords.longitude,
              formatted: `${cepData.logradouro || cepData.bairro || ''}, ${cepData.localidade}, ${cepData.uf} (CEP ${cep})`,
              confidence
            };
          } else {
            logger.warn(`⚠️ Estratégia ${index + 1} resulta em coordenadas fora do Brasil`, {
              address,
              coords,
              validation
            });
          }
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
        logger.warn(`❌ Estratégia ${index + 1} falhou`, { address, error: errorMessage });
      }
    }

    throw new Error(`Todas as estratégias de geocodificação falharam para o CEP ${cep}`);
  }

  /**
   * Verifica e sugere correções para coordenadas inválidas
   */
  async validateAndSuggestCorrection(
    input: string | Location,
    options?: GeocodingOptions
  ): Promise<{
    original: GeocodingResult & { brazilValidation?: GeographicValidationResult };
    suggestion?: {
      correctedCoordinates: Coordinates;
      message: string;
    };
  }> {
    const original = await this.geocodeWithBrazilValidation(input, options);

    if (original && original.latitude && original.longitude && original.brazilValidation) {
      if (!original.brazilValidation.isWithinBrazil) {
        const coordinates: Coordinates = {
          latitude: original.latitude,
          longitude: original.longitude
        };

        const correction = GeographicValidator.suggestCorrection(coordinates);

        if (correction.correctedCoordinates) {
          return {
            original,
            suggestion: {
              correctedCoordinates: correction.correctedCoordinates,
              message: correction.suggestion
            }
          };
        }
      }
    }

    return { original };
  }
}

// Instância singleton
export const hybridGeocodingService = new HybridGeocodingService();

// Função de conveniência para uso direto
export async function geocodeHybrid(
  input: string | Location,
  options?: GeocodingOptions
): Promise<GeocodingResult> {
  return await hybridGeocodingService.geocode(input, options);
}

// Função de conveniência para geocodificação com validação brasileira
export async function geocodeWithBrazilValidation(
  input: string | Location,
  options?: GeocodingOptions
): Promise<GeocodingResult & { brazilValidation?: GeographicValidationResult }> {
  return await hybridGeocodingService.geocodeWithBrazilValidation(input, options);
}

// Função de conveniência para validação e sugestão de correção
export async function validateAndSuggestCorrection(
  input: string | Location,
  options?: GeocodingOptions
): Promise<{
  original: GeocodingResult & { brazilValidation?: GeographicValidationResult };
  suggestion?: {
    correctedCoordinates: Coordinates;
    message: string;
  };
}> {
  return await hybridGeocodingService.validateAndSuggestCorrection(input, options);
}

// Função de conveniência para geocodificação com fallback brasileiro
export async function geocodeWithBrazilFallback(
  input: string | Location,
  options?: GeocodingOptions
): Promise<GeocodingResult & {
  brazilValidation?: GeographicValidationResult;
  usedFallback?: boolean;
  fallbackReason?: string;
  increasedTolerance?: boolean;
}> {
  return await hybridGeocodingService.geocodeWithBrazilFallback(input, options);
}